import { useEffect, useState } from 'react';
import { StatusBar } from '@capacitor/status-bar';
import { logger } from '../utils/logger';
import { getPlatform, isCapacitorAvailable } from '../capacitor-utils';

interface StatusBarInfo {
  isNativeApp: boolean;
  statusBarHeight: number;
  navigationBarHeight: number;
  getPlatform: () => 'android' | 'ios' | 'web';
}

// Get typed platform safely
const getPlatformSafe = (): 'android' | 'ios' | 'web' => {
  const platform = getPlatform();
  if (platform === 'android') return 'android';
  if (platform === 'ios') return 'ios';
  return 'web';
};

/**
 * Custom hook to get status bar and navigation bar heights
 * Uses the Capacitor StatusBar plugin to get actual measurements
 */
export function useStatusBarInfo(): StatusBarInfo {
  // Initialize with default values but check if we're in a Capacitor app immediately
  // This prevents the value from changing after the first render
  const [statusBarInfo, setStatusBarInfo] = useState<StatusBarInfo>(() => {
    const isNative = isCapacitorAvailable();
    const platform = getPlatformSafe();

    return {
      isNativeApp: isNative,
      statusBarHeight: platform === 'android' ? 24 : 47,
      navigationBarHeight: platform === 'android' ? 48 : 34,
      getPlatform: () => platform,
    };
  });

  useEffect(() => {
    // Don't try to get detailed info if we're not in a native app
    if (!statusBarInfo.isNativeApp) return;

    // Get actual measurements asynchronously
    const getDetailedInfo = async () => {
      try {
        const platform = statusBarInfo.getPlatform();
        let sbHeight = platform === 'android' ? 24 : 47;
        const navHeight = platform === 'android' ? 48 : 34;

        // Get actual status bar height for Android
        if (platform === 'android') {
          try {
            const info = await StatusBar.getInfo();
            // @ts-ignore
            if (info && typeof info.height === 'number') {
              // @ts-ignore
              sbHeight = info.height;
            }
          } catch (err) {
            logger.error('Error getting Android status bar info', err);
          }
        }

        // Only update state if something changed
        if (
          sbHeight !== statusBarInfo.statusBarHeight ||
          navHeight !== statusBarInfo.navigationBarHeight
        ) {
          setStatusBarInfo(prev => ({
            ...prev,
            statusBarHeight: sbHeight,
            navigationBarHeight: navHeight,
          }));
        }
      } catch (error) {
        logger.error('Error getting detailed status bar info', error);
      }
    };

    getDetailedInfo();
  }, [statusBarInfo.isNativeApp, statusBarInfo.getPlatform]);

  return statusBarInfo;
}
